// @ts-check
// Atalhos globais (doc 07, seção 14; doc 15, seção 15; contrato 08-contrato-comandos-atalhos): Cmd/Ctrl+K, "/",
// "?" e as sequências G + letra. Puro: decide o que uma tecla significa a partir do evento e da tecla pendente; quem
// escuta o documento é client/src/modules/command-menu/global-hotkeys.js. Teclas simples nunca disparam dentro de
// campos de texto nem quando outro componente (célula da tabela, card, diálogo) já tratou a tecla.

// tempo para a segunda tecla da sequência; depois disso o "g" esquecido não sequestra a próxima letra
export const SEQUENCE_TIMEOUT_MILLISECONDS = 1500;
export const SEQUENCE_FIRST_KEY = 'g';

/**
 * Destinos dos atalhos G + letra, na ordem do menu lateral. `object`: lista do objeto (a primeira entrada do menu
 * desse objeto, por exemplo a view "Funil de captação"); `link`: tela própria, visível se estiver no menu ou se a
 * flag permitir (`anyFlag`: basta uma). `module` (3.0.0): só com o módulo ligado na escola, esteja ou não no menu.
 * @type {ReadonlyArray<{ key: string, labelKey: string, objectNameSingular?: string, link?: string, flag?: string, anyFlag?: string[], requiresMenuItem?: boolean,
 *   module?: string }>}
 */
export const GO_TO_SHORTCUTS = Object.freeze([
  { key: 'f', labelKey: 'commands.goTo.funnel', objectNameSingular: 'enrollmentOpportunity' },
  { key: 'p', labelKey: 'commands.goTo.people', objectNameSingular: 'person' },
  { key: 'a', labelKey: 'commands.goTo.households', objectNameSingular: 'household' },
  { key: 't', labelKey: 'commands.goTo.tasks', objectNameSingular: 'task' },
  { key: 'm', labelKey: 'commands.goTo.enrollments', objectNameSingular: 'enrollment' },
  { key: 'c', labelKey: 'commands.goTo.conversations', link: '/conversations', flag: 'CONVERSATIONS' },
  { key: 'd', labelKey: 'commands.goTo.dashboards', link: '/dashboards', flag: 'DASHBOARDS' },
  // menu Campanhas (2.0.0), antes da Coordenação como na gaveta: basta uma das flags; /campaigns abre a primeira tela permitida
  { key: 'n', labelKey: 'commands.goTo.campaigns', link: '/campaigns', anyFlag: ['EMAIL_CAMPAIGNS', 'WHATSAPP_CAMPAIGNS', 'WORKFLOWS'] },
  { key: 'l', labelKey: 'commands.goTo.automationFlows', link: '/campaigns/flows', flag: 'WORKFLOWS' },
  // módulo Atendimento (3.0.0, contrato 10 de atendimento, seção 25), depois das Campanhas como na gaveta
  { key: 'h', labelKey: 'commands.goTo.serviceInbox', link: '/service', anyFlag: ['SERVICE_DESK', 'SERVICE_DESK_SETTINGS'], module: 'SERVICE_DESK' },
  { key: 'b', labelKey: 'commands.goTo.serviceBots', link: '/service/bots', flag: 'SERVICE_DESK_SETTINGS', module: 'SERVICE_DESK' },
  { key: 'v', labelKey: 'commands.goTo.visits', link: '/visits', requiresMenuItem: true },
  { key: 'e', labelKey: 'commands.goTo.calendar', link: '/calendar', requiresMenuItem: true },
  { key: 's', labelKey: 'commands.goTo.settings', link: '/settings/profile' },
]);

/**
 * Atalhos G + letra disponíveis para o papel: objeto legível no menu, tela própria com a flag ou no menu. Atalho de
 * módulo só aparece com `isModuleEnabled` dizendo que o módulo está ligado (sem ele, como na 2.0.0, não aparece).
 * @param {{ navigationItems: { href: string, objectNameSingular?: string | null }[], readableObjectNames: Set<string>,
 *   hasPermissionFlag: (flag: string) => boolean, isModuleEnabled?: (moduleKey: string) => boolean }} input
 * @returns {{ key: string, labelKey: string, href: string }[]}
 */
export function resolveGoToShortcuts({ navigationItems, readableObjectNames, hasPermissionFlag, isModuleEnabled }) {
  /** @type {{ key: string, labelKey: string, href: string }[]} */
  const shortcuts = [];
  for (const shortcut of GO_TO_SHORTCUTS) {
    if (shortcut.module && !isModuleEnabled?.(shortcut.module)) continue;
    if (shortcut.objectNameSingular) {
      if (!readableObjectNames.has(shortcut.objectNameSingular)) continue;
      const menuItem = navigationItems.find((item) => item.objectNameSingular === shortcut.objectNameSingular);
      // sem item no menu, a lista principal do objeto
      shortcuts.push({ key: shortcut.key, labelKey: shortcut.labelKey, href: menuItem?.href ?? `/objects/${pluralFallback(shortcut.objectNameSingular)}` });
      continue;
    }
    const link = /** @type {string} */ (shortcut.link);
    const isInMenu = navigationItems.some((item) => item.href.split('?')[0] === link);
    if (shortcut.requiresMenuItem && !isInMenu) continue;
    if (shortcut.flag && !hasPermissionFlag(shortcut.flag) && !isInMenu) continue;
    if (shortcut.anyFlag && !shortcut.anyFlag.some((flag) => hasPermissionFlag(flag)) && !isInMenu) continue;
    shortcuts.push({ key: shortcut.key, labelKey: shortcut.labelKey, href: link });
  }
  return shortcuts;
}

// os objetos do pacote têm plural regular; o href real vem do menu quando o item existe
const PLURAL_FALLBACKS = /** @type {Record<string, string>} */ ({ person: 'people', enrollmentOpportunity: 'enrollmentOpportunities', household: 'households', task: 'tasks', enrollment: 'enrollments' });
/** @param {string} nameSingular */
const pluralFallback = (nameSingular) => PLURAL_FALLBACKS[nameSingular] ?? `${nameSingular}s`;

/**
 * @param {EventTarget | null} target
 */
export function isTypingTarget(target) {
  const element = /** @type {any} */ (target);
  if (!element || typeof element !== 'object') return false;
  const tagName = String(element.tagName ?? '').toUpperCase();
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return true;
  if (element.isContentEditable) return true;
  // editor rico ou campo dentro de algo editável
  return typeof element.closest === 'function' && Boolean(element.closest('[contenteditable="true"], [role="textbox"]'));
}

/**
 * @typedef {{ key: string, metaKey?: boolean, ctrlKey?: boolean, altKey?: boolean, shiftKey?: boolean, isComposing?: boolean,
 *   defaultPrevented?: boolean, target?: EventTarget | null }} HotkeyEvent
 * @typedef {{ pendingKey: string | null, pendingSince: number }} SequenceState
 * @typedef {{ type: 'none' } | { type: 'toggleCommandMenu' } | { type: 'openSearch' } | { type: 'toggleShortcutHelp' }
 *   | { type: 'startSequence' } | { type: 'goTo', key: string }} HotkeyAction
 */

/**
 * O que a tecla significa. `isOverlayOpen`: diálogo, menu suspenso ou painel de atalhos aberto (eles tratam as
 * próprias teclas); só Cmd/Ctrl+K passa por cima, como no Twenty.
 * @param {HotkeyEvent} event @param {SequenceState} sequence @param {{ now: number, isOverlayOpen?: boolean }} options
 * @returns {HotkeyAction}
 */
export function interpretGlobalKeydown(event, sequence, { now, isOverlayOpen = false }) {
  if (event.isComposing) return { type: 'none' };
  const hasModifier = Boolean(event.metaKey || event.ctrlKey);
  if (hasModifier && !event.altKey && event.key.toLowerCase() === 'k') return { type: 'toggleCommandMenu' };
  if (event.defaultPrevented || isTypingTarget(event.target ?? null) || isOverlayOpen) return { type: 'none' };
  if (hasModifier || event.altKey) return { type: 'none' };
  const key = event.key.toLowerCase();
  const isPending = sequence.pendingKey === SEQUENCE_FIRST_KEY && now - sequence.pendingSince <= SEQUENCE_TIMEOUT_MILLISECONDS;
  if (isPending && /^[a-z]$/.test(key) && !event.shiftKey) return { type: 'goTo', key };
  if (event.key === '?') return { type: 'toggleShortcutHelp' };
  if (event.key === '/') return { type: 'openSearch' };
  if (key === SEQUENCE_FIRST_KEY && !event.shiftKey) return { type: 'startSequence' };
  return { type: 'none' };
}

/**
 * Próximo estado da sequência depois da tecla.
 * @param {HotkeyAction} action @param {number} now
 * @returns {SequenceState}
 */
export function nextSequenceState(action, now) {
  return action.type === 'startSequence' ? { pendingKey: SEQUENCE_FIRST_KEY, pendingSince: now } : { pendingKey: null, pendingSince: 0 };
}

/**
 * Grupos do painel de atalhos (tecla ?), com os atalhos que existem de verdade nesta tela: gerais, navegação G + letra
 * do papel, tabela, kanban, menu de comandos e, na Caixa de atendimento (3.0.0), os atalhos dela.
 * @param {{ goToShortcuts: { key: string, labelKey: string }[], modifierLabel: string,
 *   serviceInboxShortcuts?: ReadonlyArray<{ key: string, action: string }> }} input
 *   serviceInboxShortcuts: os atalhos da Caixa, só quando a tela aberta é a Caixa
 * @returns {{ titleKey: string, items: { labelKey: string, keys: string[], isSequence?: boolean, labelParams?: Record<string, unknown> }[] }[]}
 */
export function buildShortcutHelpGroups({ goToShortcuts, modifierLabel, serviceInboxShortcuts = [] }) {
  return [
    {
      titleKey: 'serviceDesk.shortcuts.title',
      items: serviceInboxShortcuts.map((shortcut) => ({ labelKey: `serviceDesk.shortcuts.actions.${shortcut.action}`, keys: [shortcut.key.toUpperCase()] })),
    },
    {
      titleKey: 'commands.shortcuts.groups.general',
      items: [
        { labelKey: 'commands.shortcuts.items.commandMenu', keys: [modifierLabel, 'K'] },
        { labelKey: 'commands.shortcuts.items.search', keys: ['/'] },
        { labelKey: 'commands.shortcuts.items.help', keys: ['?'] },
        { labelKey: 'commands.shortcuts.items.contextMenu', keys: ['commands.shortcuts.keys.rightClick'] },
      ],
    },
    {
      titleKey: 'commands.shortcuts.groups.navigation',
      items: goToShortcuts.map((shortcut) => ({ labelKey: shortcut.labelKey, keys: ['G', shortcut.key.toUpperCase()], isSequence: true })),
    },
    {
      titleKey: 'commands.shortcuts.groups.table',
      items: [
        { labelKey: 'commands.shortcuts.items.moveFocus', keys: ['↑', '↓', '←', '→'] },
        { labelKey: 'commands.shortcuts.items.editCell', keys: ['↵'] },
        { labelKey: 'commands.shortcuts.items.selectRow', keys: ['X'] },
        { labelKey: 'commands.shortcuts.items.selectRange', keys: ['⇧', 'X'] },
        { labelKey: 'commands.shortcuts.items.selectAll', keys: [modifierLabel, 'A'] },
        { labelKey: 'commands.shortcuts.items.openRecord', keys: [modifierLabel, '↵'] },
        { labelKey: 'commands.shortcuts.items.clearCell', keys: ['Delete'] },
        { labelKey: 'commands.shortcuts.items.clearSelection', keys: ['Esc'] },
      ],
    },
    {
      titleKey: 'commands.shortcuts.groups.board',
      items: [
        { labelKey: 'commands.shortcuts.items.clearSelection', keys: ['Esc'] },
      ],
    },
    // editor de fluxo (frente 2D da 2.0.0; contrato 09 de fluxos, seção 17): o canvas e as barras do editor
    {
      titleKey: 'flows.shortcuts.group',
      items: [
        { labelKey: 'flows.shortcuts.items.move', keys: ['←', '→', '↑', '↓'] },
        { labelKey: 'flows.shortcuts.items.firstLast', keys: ['Home', 'End'] },
        { labelKey: 'flows.shortcuts.items.open', keys: ['↵'] },
        { labelKey: 'flows.shortcuts.items.insert', keys: ['A'] },
        { labelKey: 'flows.shortcuts.items.delete', keys: ['Delete'] },
        { labelKey: 'flows.shortcuts.items.undo', keys: [modifierLabel, 'Z'] },
        { labelKey: 'flows.shortcuts.items.redo', keys: [modifierLabel, '⇧', 'Z'] },
        { labelKey: 'flows.shortcuts.items.save', keys: [modifierLabel, 'S'] },
        { labelKey: 'flows.shortcuts.items.zoom', keys: ['+', '-', '0'] },
        { labelKey: 'flows.shortcuts.items.close', keys: ['Esc'] },
      ],
    },
    {
      titleKey: 'commands.shortcuts.groups.commandMenu',
      items: [
        { labelKey: 'commands.shortcuts.items.moveInList', keys: ['↑', '↓'] },
        { labelKey: 'commands.shortcuts.items.runItem', keys: ['↵'] },
        { labelKey: 'commands.shortcuts.items.closeOrBack', keys: ['Esc'] },
        { labelKey: 'commands.shortcuts.items.backWhenEmpty', keys: ['⌫'] },
      ],
    },
  ].filter((group) => group.items.length > 0);
}
